import { shouldTableSortByRenewTime } from 'owa-mail-list-response-processor';
import { isNudgedRow } from 'owa-mail-nudge-store';
import {
    MailRowDataPropertyGetter,
    TableView,
    getSortByForTable,
    MailSortHelper,
} from 'owa-mail-list-store';

/**
 * Compare the date time of two rows
 * @param targetLastDeliveryOrRenewTimeStamp the lastDeliveryOrRenewTimeStamp of the conversation to insert
 * @param targetLastDeliveryTimeStamp the lastDeliveryTimeStamp of the conversation to insert
 * @param rowKeyOfReferenceRow the conversation rowKey of the reference row
 * @param tableView the table view
 * @returns
 * 1) < 0 if reference row is earlier than target row
 * 2) 0 if have the same time
 * 3) > 0 if reference row is later than target row
 */
function compareRowDateTime(
    targetLastDeliveryOrRenewTimeStamp: string,
    targetLastDeliveryTimeStamp: string,
    rowKeyOfReferenceRow: string,
    tableView: TableView
): number {
    const diffDeliveryUTCTime =
        new Date(
            MailRowDataPropertyGetter.getLastDeliveryTimeStamp(rowKeyOfReferenceRow, tableView)
        ).getTime() - new Date(targetLastDeliveryTimeStamp).getTime();

    if (shouldTableSortByRenewTime(tableView.tableQuery)) {
        // If table supports pin, use renew time as primary sort, and delivery time as secondary sort
        const diffRenewTimeInUTC =
            new Date(
                MailRowDataPropertyGetter.getLastDeliveryOrRenewTimeStamp(
                    rowKeyOfReferenceRow,
                    tableView
                )
            ).getTime() - new Date(targetLastDeliveryOrRenewTimeStamp).getTime();
        return diffRenewTimeInUTC != 0 ? diffRenewTimeInUTC : diffDeliveryUTCTime;
    }

    return diffDeliveryUTCTime;
}

/**
 * Computes the insert index for the row in date view sort
 * @param lastDeliveryOrRenewTimeStamp the lastDeliveryOrRenewTimeStamp of the conversation to insert
 * @param lastDeliveryTimeStamp the lastDeliveryTimeStamp of the conversation to insert
 * @param startIndex the start index to scan from. If we're pinning, start from top, if unpinning, it can only be moved to at or below the old index
 * @returns insert index for the row, -1 if could not place the row
 */
export default function findIndexToInsertAt(
    lastDeliveryOrRenewTimeStamp: string,
    lastDeliveryTimeStamp: string,
    tableView: TableView,
    startIndex: number,
    skipNudgedRows?: boolean
): number {
    // Handle the case when tableView is empty
    // We will insert this item at index 0 to begin
    if (tableView.rowKeys.length == 0) {
        return 0;
    }

    // Scan from top until we find row that's later\earlier than the row depending on the sort direction
    // VSO: 18945 - Add notification support for the multivalued sort
    const latestFirst =
        getSortByForTable(tableView).sortDirection == MailSortHelper.DESCENDING_SORT_DIR;
    for (let i = startIndex; i < tableView.rowKeys.length; i++) {
        const rowKey = tableView.rowKeys[i];
        if (skipNudgedRows && isNudgedRow(rowKey, tableView.id)) {
            // nudged rows are positioned out of date time sort order
            // so we should not compare with their time stamp when
            // finding the correct index to insert
            continue;
        }

        const rowDateTimeDiff = compareRowDateTime(
            lastDeliveryOrRenewTimeStamp,
            lastDeliveryTimeStamp,
            rowKey,
            tableView
        );

        if (latestFirst ? rowDateTimeDiff <= 0 : rowDateTimeDiff > 0) {
            return i;
        }
    }

    // There is no row in table that's earlier or later depending on the date time direction,
    // than the row to insert. This could happen if e.g user unpins an old conversation in date time
    // descending sort
    if (tableView.rowKeys.length == tableView.totalRowsInView) {
        // Insert the row to the end of the table in case client has loaded all rows from server.
        return tableView.rowKeys.length;
    }

    // Otherwise returns -1 as we couldn't find an index to insert the conversation
    return -1;
}
